import React, { useEffect, useLayoutEffect, useRef } from "react";

/**
 * One way to hide a sentence. A native <details> with the Workbench summary
 * row: chevron · label · optional right-hand meta (a figure or a state word).
 *
 * `open` is the default state. `forceOpen` overrides it and re-opens the
 * panel whenever it flips to true — for content that must be seen (a
 * warning appeared, a trace action asked to focus a field inside).
 *
 * Props only ever OPEN the panel; they never close it. The open attribute is
 * not a controlled prop: when the value inside the panel is what drives it
 * (a quoted amount being retyped, an hour field passing through 0), a
 * controlled `open` going false collapsed the panel under the estimator's
 * cursor, dropped focus and lost the next keystrokes. Closing is the user's.
 * Native <details> keeps its content in the DOM while closed, so tests can
 * still query it and screen readers can still reach it.
 */
function Disclosure({
  summary,
  meta = null,
  open = false,
  forceOpen = false,
  className = "",
  id,
  onToggle,
  children,
}) {
  const ref = useRef(null);
  const shouldOpen = Boolean(open || forceOpen);

  // Before paint on mount, so a default-open panel never flashes closed.
  useLayoutEffect(() => {
    if (shouldOpen && ref.current) ref.current.open = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Each rising edge opens, independently: a warning appearing re-opens a
  // panel the user closed even when `open` (the default) was already true.
  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.open = true;
  }, [open]);
  useEffect(() => {
    if (forceOpen && ref.current && !ref.current.open) ref.current.open = true;
  }, [forceOpen]);

  return (
    <details
      ref={ref}
      id={id}
      className={`wb-disclosure${className ? ` ${className}` : ""}`}
      onToggle={onToggle}
    >
      <summary className="wb-disclosure__summary">
        <span className="wb-disclosure__tw" aria-hidden="true">
          ▶
        </span>
        <span className="wb-disclosure__label">{summary}</span>
        {meta !== null && meta !== undefined ? (
          <span className="wb-disclosure__meta wb-num">{meta}</span>
        ) : null}
      </summary>
      <div className="wb-disclosure__body">{children}</div>
    </details>
  );
}

export default Disclosure;
